import { useEffect, useRef } from 'react';
import { reportWebVitalsLightweight as reportWebVitals } from '@app/analytics/lightweightReporters';

type WebVitalsMetrics = {
  cumulativeLayoutShift?: number;
  firstContentfulPaint?: number;
  largestContentfulPaint?: number;
  timeToFirstByte?: number;
};

const STORAGE_KEY = 'storageone-web-vitals-reported';

export const useWebVitals = () => {
  const metricsRef = useRef<WebVitalsMetrics>({});
  const reportedRef = useRef(false);

  useEffect(() => {
    // Check if already reported this session
    if (sessionStorage.getItem(STORAGE_KEY)) {
      return;
    }

    const tryReport = () => {
      // Report when we have LCP and CLS
      if (
        metricsRef.current.largestContentfulPaint !== undefined &&
        metricsRef.current.cumulativeLayoutShift !== undefined &&
        !reportedRef.current
      ) {
        reportedRef.current = true;
        sessionStorage.setItem(STORAGE_KEY, 'true');

        reportWebVitals({
          largestContentfulPaint: metricsRef.current.largestContentfulPaint,
          firstContentfulPaint: metricsRef.current.firstContentfulPaint,
          timeToFirstByte: metricsRef.current.timeToFirstByte,
          cumulativeLayoutShift: metricsRef.current.cumulativeLayoutShift
        });
      }
    };

    // Get TTFB from Navigation Timing API
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      metricsRef.current.timeToFirstByte = navEntry.responseStart;
    }

    // Get FCP from Paint Timing API
    const paintEntries = performance.getEntriesByType('paint');
    const fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    if (fcpEntry) {
      metricsRef.current.firstContentfulPaint = fcpEntry.startTime;
    }

    // Observe LCP
    let lcpObserver: PerformanceObserver | undefined;
    if ('PerformanceObserver' in window) {
      try {
        lcpObserver = new PerformanceObserver(entryList => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          metricsRef.current.largestContentfulPaint = lastEntry.startTime;
          tryReport();
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch {
        // LCP not supported
      }
    }

    // Observe CLS
    let clsObserver: PerformanceObserver | undefined;
    let clsValue = 0;
    if ('PerformanceObserver' in window) {
      try {
        clsObserver = new PerformanceObserver(entryList => {
          for (const entry of entryList.getEntries()) {
            const layoutShift = entry as PerformanceEntry & {
              hadRecentInput?: boolean;
              value?: number;
            };
            if (!layoutShift.hadRecentInput && layoutShift.value) {
              clsValue += layoutShift.value;
            }
          }
          metricsRef.current.cumulativeLayoutShift = clsValue;
          tryReport();
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });
      } catch {
        // CLS not supported - set to 0 to allow reporting
        metricsRef.current.cumulativeLayoutShift = 0;
        tryReport();
      }
    }

    return () => {
      lcpObserver?.disconnect();
      clsObserver?.disconnect();
    };
  }, []);
};
